/**
 * Fiche de recueil du besoin employeur (CDC §5.1), en fenêtre.
 *
 * Extraite de l'écran « Entreprises & prospection » : le dossier d'une
 * organisation recueille un besoin exactement de la même façon, et une seconde
 * implémentation aurait fini par ne plus collecter la même chose que la fiche
 * papier. Le formulaire, ses règles de saisie et son vocabulaire vivent déjà
 * dans `ChampsFicheBesoin` et `lib/fiche-besoin` : ce composant n'est que la
 * fenêtre et l'envoi.
 */

import { useState } from 'react';
import { BoutonMint, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { ChampsFicheBesoin } from './ChampsFicheBesoin';
import {
  emailValide,
  nombrePostes,
  postesValides,
  tachesRemplies,
  valeursInitiales,
  type ValeursFiche,
} from '../../lib/fiche-besoin';
import { useAuth } from '../../auth/useAuth';
import type { EntreeBesoin, TypeContratBesoin } from '../../lib/useEntreprises';

/** Employeur dont le besoin est recueilli : rappelé, jamais ressaisi. */
export interface EmployeurDuBesoin {
  id: string;
  denomination: string;
  siret: string;
  telephone: string | null;
  email: string | null;
}

export function ModaleRecueilBesoin({
  employeur,
  formations,
  typesContrat,
  enCours,
  erreur,
  onFermer,
  onRecueillir,
  /** Masque « Enregistrer et lancer le matching » là où le matching n'est pas à portée. */
  avecMatching = true,
}: {
  employeur: EmployeurDuBesoin | null;
  formations: { code: string; titre: string }[];
  typesContrat: { valeur: TypeContratBesoin; libelle: string }[];
  enCours: boolean;
  erreur: string | null;
  onFermer: () => void;
  onRecueillir: (entree: EntreeBesoin, lancerMatching: boolean) => void;
  avecMatching?: boolean;
}): JSX.Element | null {
  if (!employeur) return null;

  return (
    <Modale
      ouverte
      titre="Fiche de recueil du besoin employeur"
      sousTitre={employeur.denomination}
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-4 p-4 sm:p-6">
        {erreur && <MessageErreur message={erreur} />}
        <FormulaireRecueil
          key={employeur.id}
          employeur={employeur}
          formations={formations}
          typesContrat={typesContrat}
          enCours={enCours}
          avecMatching={avecMatching}
          onAnnuler={onFermer}
          onRecueillir={onRecueillir}
        />
      </div>
    </Modale>
  );
}

function FormulaireRecueil({
  employeur,
  formations,
  typesContrat,
  enCours,
  avecMatching,
  onAnnuler,
  onRecueillir,
}: {
  employeur: EmployeurDuBesoin;
  formations: { code: string; titre: string }[];
  typesContrat: { valeur: TypeContratBesoin; libelle: string }[];
  enCours: boolean;
  avecMatching: boolean;
  onAnnuler: () => void;
  onRecueillir: (entree: EntreeBesoin, lancerMatching: boolean) => void;
}): JSX.Element {
  // Le commercial est pré-rempli avec le compte connecté : c'est lui qui
  // recueille dans l'immense majorité des cas, et le champ reste corrigeable
  // quand la fiche papier vient d'un collègue.
  const { utilisateur } = useAuth();
  const commercialParDefaut = utilisateur
    ? `${utilisateur.prenom} ${utilisateur.nom}`.trim()
    : '';
  const [valeurs, setValeurs] = useState<ValeursFiche>(() =>
    valeursInitiales(null, commercialParDefaut),
  );

  const valide = postesValides(valeurs.postes) && emailValide(valeurs.candidatEmail);
  const taches = tachesRemplies(valeurs.taches);

  // Le bouton pressé décide de la suite : rester où l'on est ou enchaîner sur
  // le matching. Le besoin, lui, est transmis au pôle commercial dans les deux
  // cas — seul le déplacement à l'écran change.
  const envoyer = (lancerMatching: boolean): void => {
    if (enCours || !valide) return;
    onRecueillir(
      {
        nombrePostes: nombrePostes(valeurs.postes),
        // Les champs vides sont omis, pas envoyés vides : au recueil, rien
        // n'a encore été saisi, il n'y a donc rien à effacer.
        ...(valeurs.formationCode ? { formationCode: valeurs.formationCode } : {}),
        ...(valeurs.dateSouhaitee ? { dateSouhaitee: valeurs.dateSouhaitee } : {}),
        ...(valeurs.profilRecherche.trim()
          ? { profilRecherche: valeurs.profilRecherche.trim() }
          : {}),
        ouvertRqth: valeurs.rqth,
        ...(valeurs.dateDemande ? { dateDemande: valeurs.dateDemande } : {}),
        ...(valeurs.commercial.trim() ? { commercial: valeurs.commercial.trim() } : {}),
        ...(valeurs.typeContrat ? { typeContrat: valeurs.typeContrat } : {}),
        ...(taches.length > 0 ? { tachesOffre: taches } : {}),
        ...(valeurs.faisabilite === '' ? {} : { faisabilite: valeurs.faisabilite === 'oui' }),
        ...(valeurs.motifFaisabilite.trim()
          ? { motifFaisabilite: valeurs.motifFaisabilite.trim() }
          : {}),
        ...(valeurs.candidatNom.trim() ? { candidatNom: valeurs.candidatNom.trim() } : {}),
        ...(valeurs.candidatTelephone.trim()
          ? { candidatTelephone: valeurs.candidatTelephone.trim() }
          : {}),
        ...(valeurs.candidatEmail.trim() ? { candidatEmail: valeurs.candidatEmail.trim() } : {}),
      },
      lancerMatching,
    );
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        envoyer(avecMatching);
      }}
    >
      <ChampsFicheBesoin
        valeurs={valeurs}
        onChange={setValeurs}
        formations={formations}
        typesContrat={typesContrat}
        employeur={{
          denomination: employeur.denomination,
          siret: employeur.siret,
          telephone: employeur.telephone,
          email: employeur.email,
        }}
      />

      <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
        <BoutonSecondaire onClick={onAnnuler} disabled={enCours}>
          Annuler
        </BoutonSecondaire>
        {avecMatching ? (
          <>
            <BoutonSecondaire onClick={() => envoyer(false)} disabled={enCours || !valide}>
              {enCours ? 'Enregistrement…' : 'Enregistrer seulement'}
            </BoutonSecondaire>
            <BoutonMint type="submit" disabled={enCours || !valide}>
              {enCours ? 'Enregistrement…' : 'Enregistrer et lancer le matching'}
            </BoutonMint>
          </>
        ) : (
          <BoutonMint type="submit" disabled={enCours || !valide}>
            {enCours ? 'Enregistrement…' : 'Enregistrer le besoin'}
          </BoutonMint>
        )}
      </div>
    </form>
  );
}
